import React, { useState } from 'react';
import { Shield, Zap, Globe, Users, BarChart3, ArrowRight } from 'lucide-react';

const translations = {
  en: {
    nav: { features: 'Features', useCases: 'Use Cases', about: 'About', start: 'Get Started' },
    hero: {
      title: 'A Platform for Managing Connected Systems',
      desc: 'Bitacto is a flexible foundation for building, operating, and evolving connected devices and software systems — whatever direction your product takes.',
      cta1: 'Get Started', cta2: 'Explore',
    },
    featuresTitle: 'What It Can Become',
    featuresDesc: 'A general set of capabilities that can adapt to many technical and business needs.',
    features: [
      { title: 'Security First', desc: 'Designed with security, control, and reliability in mind.' },
      { title: 'Efficient by Design', desc: 'Built to scale from small experiments to large deployments.' },
      { title: 'Technology Agnostic', desc: 'Works across architectures and environments.' },
      { title: 'Remote Interaction', desc: 'Observe and interact with systems anywhere.' },
      { title: 'Insights & Visibility', desc: 'High-level understanding of system behavior.' },
    ],
    useCasesTitle: 'Open-Ended by Nature',
    useCases: [
      { title: 'Connected Devices', desc: 'Devices that require coordination and updates.' },
      { title: 'Edge & Embedded Systems', desc: 'Systems operating close to hardware or data.' },
      { title: 'Future Products', desc: 'A foundation for ideas not yet defined.' },
    ],
    ctaTitle: 'Built for What Comes Next',
    ctaDesc: 'Intentionally broad — so you can shape it later.',
    contact: 'Contact',
    footer: '© 2025 Bitacto · Flexible technology platform',
  },
  pl: {
    nav: { features: 'Możliwości', useCases: 'Zastosowania', about: 'O projekcie', start: 'Zacznij' },
    hero: {
      title: 'Platforma do zarządzania połączonymi systemami',
      desc: 'Bitacto to elastyczna baza do budowy i rozwoju systemów — niezależnie od przyszłego kierunku projektu.',
      cta1: 'Zacznij', cta2: 'Poznaj',
    },
    featuresTitle: 'Potencjalne możliwości',
    featuresDesc: 'Ogólne cechy możliwe do dopasowania w przyszłości.',
    features: [
      { title: 'Bezpieczeństwo', desc: 'Stabilność i kontrola jako fundament.' },
      { title: 'Skalowalność', desc: 'Od prototypu do większej skali.' },
      { title: 'Niezależność technologiczna', desc: 'Różne architektury i środowiska.' },
      { title: 'Zdalny dostęp', desc: 'Wgląd w systemy z dowolnego miejsca.' },
      { title: 'Widoczność', desc: 'Lepsze zrozumienie działania całości.' },
    ],
    useCasesTitle: 'Otwarte zastosowania',
    useCases: [
      { title: 'Urządzenia połączone', desc: 'Sprzęt i systemy wymagające koordynacji.' },
      { title: 'Systemy wbudowane', desc: 'Rozwiązania blisko sprzętu lub danych.' },
      { title: 'Przyszłe pomysły', desc: 'Miejsce na jeszcze nieokreślone projekty.' },
    ],
    ctaTitle: 'Gotowe na przyszłość',
    ctaDesc: 'Celowo ogólne — z myślą o późniejszym doprecyzowaniu.',
    contact: 'Kontakt',
    footer: '© 2025 Bitacto · Elastyczna platforma technologiczna',
  },
  de: {
    nav: { features: 'Funktionen', useCases: 'Anwendungen', about: 'Über', start: 'Starten' },
    hero: {
      title: 'Plattform für vernetzte Systeme',
      desc: 'Bitacto ist eine flexible Grundlage für den Aufbau und Betrieb vernetzter Systeme.',
      cta1: 'Starten', cta2: 'Entdecken',
    },
    featuresTitle: 'Mögliche Funktionen',
    featuresDesc: 'Anpassbar an viele zukünftige Anforderungen.',
    features: [
      { title: 'Sicherheit', desc: 'Zuverlässigkeit und Kontrolle im Fokus.' },
      { title: 'Skalierbarkeit', desc: 'Vom Experiment bis zum Betrieb.' },
      { title: 'Technologieoffen', desc: 'Unabhängig von Plattformen.' },
      { title: 'Fernzugriff', desc: 'Systeme jederzeit einsehbar.' },
      { title: 'Transparenz', desc: 'Überblick über den Systemzustand.' },
    ],
    useCasesTitle: 'Offene Einsatzbereiche',
    useCases: [
      { title: 'Vernetzte Geräte', desc: 'Koordinierte Systeme und Geräte.' },
      { title: 'Embedded & Edge', desc: 'Nahe an Hardware und Daten.' },
      { title: 'Zukünftige Produkte', desc: 'Raum für neue Ideen.' },
    ],
    ctaTitle: 'Bereit für die Zukunft',
    ctaDesc: 'Bewusst offen gehalten.',
    contact: 'Kontakt',
    footer: '© 2025 Bitacto · Flexible Technologieplattform',
  },
  fr: {
    nav: { features: 'Fonctionnalités', useCases: 'Cas d’usage', about: 'À propos', start: 'Commencer' },
    hero: {
      title: 'Plateforme pour systèmes connectés',
      desc: 'Bitacto est une base flexible pour construire et faire évoluer des systèmes connectés.',
      cta1: 'Commencer', cta2: 'Découvrir',
    },
    featuresTitle: 'Fonctionnalités potentielles',
    featuresDesc: 'Pensé pour évoluer avec vos besoins.',
    features: [
      { title: 'Sécurité', desc: 'Fiabilité et contrôle.' },
      { title: 'Scalabilité', desc: 'De l’idée à la production.' },
      { title: 'Flexibilité', desc: 'Indépendant des technologies.' },
      { title: 'Accès distant', desc: 'Supervision à distance.' },
      { title: 'Visibilité', desc: 'Compréhension globale du système.' },
    ],
    useCasesTitle: 'Usages ouverts',
    useCases: [
      { title: 'Appareils connectés', desc: 'Coordination et évolution.' },
      { title: 'Systèmes embarqués', desc: 'Proches du matériel.' },
      { title: 'Produits futurs', desc: 'Encore à définir.' },
    ],
    ctaTitle: 'Pensé pour demain',
    ctaDesc: 'Large et adaptable par conception.',
    contact: 'Contact',
    footer: '© 2025 Bitacto · Plateforme technologique flexible',
  },
  es: {
    nav: { features: 'Funciones', useCases: 'Usos', about: 'Acerca de', start: 'Empezar' },
    hero: {
      title: 'Plataforma para sistemas conectados',
      desc: 'Bitacto es una base flexible para crear y operar sistemas conectados.',
      cta1: 'Empezar', cta2: 'Explorar',
    },
    featuresTitle: 'Capacidades potenciales',
    featuresDesc: 'Diseñado para adaptarse con el tiempo.',
    features: [
      { title: 'Seguridad', desc: 'Control y estabilidad.' },
      { title: 'Escalabilidad', desc: 'Desde pruebas hasta producción.' },
      { title: 'Flexibilidad', desc: 'Independiente de la tecnología.' },
      { title: 'Acceso remoto', desc: 'Gestión desde cualquier lugar.' },
      { title: 'Visibilidad', desc: 'Comprensión del sistema.' },
    ],
    useCasesTitle: 'Usos abiertos',
    useCases: [
      { title: 'Dispositivos conectados', desc: 'Sistemas coordinados.' },
      { title: 'Edge y embebidos', desc: 'Cerca del hardware.' },
      { title: 'Ideas futuras', desc: 'Por definir.' },
    ],
    ctaTitle: 'Preparado para el futuro',
    ctaDesc: 'Intencionalmente amplio.',
    contact: 'Contacto',
    footer: '© 2025 Bitacto · Plataforma tecnológica flexible',
  },
};

export default function App() {
  const [lang, setLang] = useState('en');
  const languages = ['en', 'pl', 'de', 'fr', 'es'];
  const t = translations[lang];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <nav className="px-6 py-4 max-w-7xl mx-auto flex justify-between items-center">
        <div className="text-2xl font-bold">Bitacto</div>
        <div className="flex items-center gap-6">
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="bg-slate-800 text-white text-sm rounded px-2 py-1">
            {languages.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
          <button className="bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-2 rounded-full text-sm">
            {t.nav.start}
          </button>
        </div>
      </nav>

      <section className="px-6 py-24 text-center max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold mb-6">{t.hero.title}</h1>
        <p className="text-xl text-gray-300 mb-10">{t.hero.desc}</p>
        <div className="flex gap-4 justify-center">
          <button className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-4 rounded-full flex items-center gap-2">
            {t.hero.cta1} <ArrowRight className="w-5 h-5" />
          </button>
          <button className="border border-gray-600 px-8 py-4 rounded-full">{t.hero.cta2}</button>
        </div>
      </section>

      <section className="px-6 py-20 max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">{t.featuresTitle}</h2>
        <p className="text-center text-gray-300 mb-12">{t.featuresDesc}</p>
        <div className="grid md:grid-cols-3 gap-8">
          {t.features.map((f, i) => (
            <div key={i} className="bg-white/5 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-300">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-20 bg-white/5">
        <h2 className="text-4xl font-bold text-center mb-12">{t.useCasesTitle}</h2>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          {t.useCases.map((u, i) => (
            <div key={i} className="p-8 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2">{u.title}</h3>
              <p className="text-gray-300">{u.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-20 text-center max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold mb-4">{t.ctaTitle}</h2>
        <p className="text-gray-300 mb-8">{t.ctaDesc}</p>
        <button className="border border-gray-600 px-8 py-4 rounded-full">{t.contact}</button>
      </section>

      <footer className="py-10 text-center text-gray-400 text-sm">{t.footer}</footer>
    </div>
  );
}

